import { useMemo, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, Link, TextField, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { appPath } from '@yourownmetric/shared'

import { useCreateProjectKeyMutation } from '@/apis/opsApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { CodeBlock } from '@/components/shared/CodeBlock'

import { isPublishableKey, shopifyCustomPixelScript } from './shopifyCustomPixel'

interface ShopifySetupGuideProps {
    projectId: string | undefined
    projectName: string | undefined
    ingestUrl: string
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Box
                sx={{
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                }}
            >
                {n}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {title}
                </Typography>
                {children}
            </Box>
        </Box>
    )
}

const Body = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {children}
    </Typography>
)

/** Shopify setup without the app: a custom pixel pasted into Shopify admin → Customer events. */
export function ShopifySetupGuide({ projectId, projectName, ingestUrl }: ShopifySetupGuideProps) {
    const [key, setKey] = useState('')
    const [createKey, createState] = useCreateProjectKeyMutation()

    const trimmed = key.trim()
    const isSecret = trimmed.startsWith('yom_sk_')
    const valid = isPublishableKey(trimmed)
    const script = useMemo(
        () => (valid ? shopifyCustomPixelScript(trimmed, ingestUrl) : ''),
        [valid, trimmed, ingestUrl],
    )

    const handleCreate = async () => {
        if (!projectId) {
            return
        }
        const r = await createKey({
            projectId,
            name: 'Shopify custom pixel',
            scopes: ['ingest'],
            sub_tenant: null,
            expires_at: null,
        })
            .unwrap()
            .catch(() => null)
        if (r) {
            setKey(r.key)
        }
    }

    return (
        <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
                Add YOM to your Shopify store yourself in about 5 minutes. You paste one piece of
                code into Shopify admin; no app to install and no theme edits.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                YOM then records page views, product views, searches, cart activity, checkout steps
                and purchases from your online store, with Shopify&apos;s cookie banner deciding
                what YOM may store.
            </Typography>

            <Step n={1} title="Get a publishable key">
                <Body>
                    The pixel needs this project&apos;s publishable key (it starts with{' '}
                    <code>yom_pk_</code>). Publishable keys can only send events, so they are safe
                    in a storefront. Create one just for Shopify, or paste a publishable key you
                    already have.
                </Body>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={!projectId || createState.isLoading}
                    >
                        {createState.isLoading ? 'Creating…' : 'Create a Shopify key'}
                    </Button>
                    <TextField
                        size="small"
                        label="Publishable key"
                        placeholder="yom_pk_live_…"
                        value={key}
                        onChange={e => setKey(e.target.value)}
                        error={trimmed !== '' && !valid}
                        helperText={
                            isSecret
                                ? 'This is a secret key. Never put it in a storefront: use a publishable key (yom_pk_…).'
                                : trimmed !== '' && !valid
                                  ? 'Paste a publishable key that starts with yom_pk_'
                                  : projectName
                                    ? `For project ${projectName}`
                                    : ' '
                        }
                        sx={{ flex: 1, minWidth: 280, '& input': { fontFamily: 'monospace' } }}
                    />
                </Box>
                {createState.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {v2ErrorMessage(createState.error)}
                    </Alert>
                )}
                {createState.isSuccess && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Key created. YOM shows it only once, so finish the steps below now (you can
                        always create another one).
                    </Alert>
                )}
            </Step>

            <Step n={2} title="Copy the pixel code">
                {script ? (
                    <>
                        <Body>
                            Your key and YOM&apos;s address are already filled in. Copy the whole
                            block with the copy button; don&apos;t change it.
                        </Body>
                        <CodeBlock
                            code={script}
                            title="YOM Analytics custom pixel"
                            language="javascript"
                            placement="shopify_guide"
                        />
                    </>
                ) : (
                    <Alert severity="info">
                        Create or paste a publishable key in step 1 and the code appears here.
                    </Alert>
                )}
            </Step>

            <Step n={3} title="Add a custom pixel in Shopify admin">
                <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
                    <Typography component="li" variant="body2">
                        In Shopify admin, click <strong>Settings</strong> (bottom left), then{' '}
                        <strong>Customer events</strong>.
                    </Typography>
                    <Typography component="li" variant="body2">
                        Click <strong>Add custom pixel</strong>, name it{' '}
                        <strong>YOM Analytics</strong> and click <strong>Add pixel</strong>.
                    </Typography>
                </Box>
            </Step>

            <Step n={4} title="Set the pixel's customer privacy">
                <Body>Under Customer privacy, choose:</Body>
                <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1.5, '& li': { mb: 0.75 } }}>
                    <Typography component="li" variant="body2">
                        <strong>Permission: Required</strong>, then tick <strong>Analytics</strong>{' '}
                        only (leave Marketing and Preferences unticked).
                    </Typography>
                    <Typography component="li" variant="body2">
                        <strong>Data sale: Data collected does not qualify as data sale</strong>.
                        YOM keeps your data for you and never sells or shares it.
                    </Typography>
                </Box>
                <Alert severity="info" icon={false}>
                    <strong>Why Required?</strong> Where the law asks for consent (for example the
                    EU and UK), Shopify then runs YOM only after a shopper allows analytics in your
                    cookie banner. Elsewhere it runs for everyone. This is the same setting the YOM
                    app uses, and the safe choice if you are unsure.
                    <br />
                    <br />
                    <strong>Not required</strong> makes Shopify run YOM for every shopper, including
                    those who declined. YOM still stores nothing for them (no visitor id, no
                    cookies, a session that ends with the page), so you see more of your traffic.
                    Pick it only if your privacy policy covers cookieless measurement without
                    consent in the places you sell to.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Selling to the EU or UK? Turn on Shopify&apos;s cookie banner in{' '}
                    <strong>Settings → Customer privacy</strong> so shoppers can allow analytics.
                </Typography>
            </Step>

            <Step n={5} title="Paste the code and connect">
                <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
                    <Typography component="li" variant="body2">
                        In the <strong>Code</strong> box, delete everything that is there and paste
                        the code from step 2.
                    </Typography>
                    <Typography component="li" variant="body2">
                        Click <strong>Save</strong>, then <strong>Connect</strong>, and confirm. The
                        pixel shows as <strong>Connected</strong> in the Customer events list.
                    </Typography>
                </Box>
            </Step>

            <Step n={6} title="Check that it works">
                <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
                    <Typography component="li" variant="body2">
                        Open your store in a private browser window. If a cookie banner appears,
                        accept it.
                    </Typography>
                    <Typography component="li" variant="body2">
                        View a product and add it to your cart. To check purchases too, place a test
                        order (for example with Shopify&apos;s Bogus Gateway or your payment
                        provider&apos;s test mode) and wait for the thank-you page.
                    </Typography>
                    <Typography component="li" variant="body2">
                        In YOM, open{' '}
                        <Link component={RouterLink} to={appPath('realtime')} underline="hover">
                            Realtime
                        </Link>{' '}
                        with this project selected. Your visit appears within a minute.
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Nothing showing? Check that the pixel says Connected, that the code was pasted
                    in full, and that you accepted the cookie banner (with Permission set to
                    Required, Shopify holds the pixel back until you do).
                </Typography>
            </Step>

            <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        What this setup does not include
                    </Typography>
                    <Body>
                        The pixel sees what happens in the shopper&apos;s browser. Compared with the
                        YOM Shopify app, which also receives Shopify&apos;s order webhooks on
                        YOM&apos;s servers:
                    </Body>
                    <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Purchases are counted only when the shopper reaches the thank-you page
                            in a browser where the pixel runs. Orders a shopper declines analytics
                            for (with Required), orders created in Shopify admin, POS and other
                            channels, and subscription renewals are not included.
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            No refunds, and no server-confirmed or paid-only order counts, so
                            revenue can differ from Shopify&apos;s Orders report.
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            No connection health on the Connected integrations card, and no purchase
                            forwarding to ad platforms (Meta, Google) through YOM destinations,
                            which needs the app&apos;s server orders.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}
